export const S3_MEDIA_PREFIX =
  "https://bairuha-bucket.s3.ap-south-1.amazonaws.com/suprabhaatham/";
/** Epaper files are backed up under suprabhaatham/epaper/ (see file-upload service). */
export const S3_EPAPER_PREFIX = S3_MEDIA_PREFIX + "epaper/";
const MARKER = "/uploads/suprabhaatham-bucket/";

/** Strip scheme://host from a URL, returning the path with no leading slash / query. */
function pathAfterOrigin(url: string): string {
  const m = url.match(/^[a-z]+:\/\/[^/]+\//i);
  const rest = m ? url.slice(m[0].length) : url.replace(/^\/+/, "");
  return rest.split("?")[0];
}

/**
 * Server-hosted media URL -> its S3 backup URL, or null if not swappable.
 *
 * Two families of media, two ways to map:
 *  - News/library uploads carry the marker /uploads/suprabhaatham-bucket/ and map
 *    to suprabhaatham/{path}.
 *  - Epaper pages/thumbs (from the file-upload service) always contain "-epaper-"
 *    in the filename and are served at {host}/{date}/{edition}/{file}. We map the
 *    path (host-agnostically) to suprabhaatham/epaper/{path}, so it works for both
 *    production (e-files.suprabhaatham.com) and local dev (localhost:3110).
 */
export function s3FallbackUrl(url?: string | null): string | null {
  if (!url || typeof url !== "string") return null;
  const i = url.indexOf(MARKER);
  if (i !== -1) {
    const path = url.slice(i + MARKER.length).split("?")[0];
    return path ? S3_MEDIA_PREFIX + path : null;
  }
  if (url.includes("-epaper-")) {
    const path = pathAfterOrigin(url);
    return path ? S3_EPAPER_PREFIX + path : null;
  }
  return null;
}

/** Reusable native <img onError> handler: swap to S3 once. */
export function onImgErrorFallback(
  e: React.SyntheticEvent<HTMLImageElement> | { currentTarget: HTMLImageElement }
) {
  const img = (e as any).currentTarget as HTMLImageElement;
  if (!img || img.dataset.s3tried) return;
  const fb = s3FallbackUrl(img.currentSrc || img.src);
  if (fb && fb !== img.src) {
    img.dataset.s3tried = "1";
    img.src = fb;
  }
}

let bodyFallbackInstalled = false;

/**
 * Install a single, idempotent, document-level capture-phase error listener so
 * that inline <img> tags inside richtext body HTML (rendered via
 * dangerouslySetInnerHTML) fall back to their S3 backup copy on load failure.
 * Safe to call from multiple preview containers — only the first call binds.
 */
export function installBodyImageFallback() {
  if (bodyFallbackInstalled || typeof document === "undefined") return;
  bodyFallbackInstalled = true;
  document.addEventListener(
    "error",
    (e: Event) => {
      const t = e.target as HTMLImageElement | null;
      if (t && t.tagName === "IMG" && !t.dataset.s3tried) {
        const fb = s3FallbackUrl(t.currentSrc || t.src);
        if (fb && fb !== t.src) {
          t.dataset.s3tried = "1";
          t.src = fb;
        }
      }
    },
    true
  );
}
